import { useQuery } from '@tanstack/react-query';
import { isPermissionRefused, type UserSearchHit } from '@citibox-panel/api-client';
import type { SearchTab } from '../model/shellState';
import { firstActiveRecipient } from '../../../lib/firstActiveRecipient';

const FIVE_MINUTES = 5 * 60 * 1000;

export interface UseAccount {
  account: UserSearchHit | undefined;
  isPending: boolean;
}

// The account data shares H2's query key (`['phone-search', <phone>]`, the phone
// being the tab key), so it reuses the hot cache when the search is still warm and
// refetches on a cache miss (refresh, GC, deep-link). `throwOnError` lets a fetch
// failure surface through the shell's FeatureErrorBoundary instead of leaving the
// account card with no data — the card itself has no error state by design. A permission
// refusal is the exception: the host replaces the whole view with its access screen, so the
// boundary's fallback would only flash a wrong reason in front of it.
export function useAccount(
  search: SearchTab,
  searchUsers: (phone: string) => Promise<UserSearchHit[]>,
): UseAccount {
  const { data, isPending } = useQuery({
    queryKey: ['phone-search', search.key],
    queryFn: () => searchUsers(search.key),
    staleTime: FIVE_MINUTES,
    throwOnError: (error) => !isPermissionRefused(error),
  });

  return { account: firstActiveRecipient(data ?? []), isPending };
}
